import { jwtDecode } from 'jwt-decode';
import { getLocalStorage, prefixedKey } from '@tools/localStorage';
import { TOKEN_STORAGE_KEY } from '@tools/constants';
import withSyncStorage from './withSyncStorage';
import { LOGOUT, FORCE_LOGOUT, LOGIN, INIT_COMPLETE } from '../actions/auth';

/*
  jwtDecode THROWS on anything that is not a well-formed JWT, and this runs at module scope — before
  React mounts, so no error boundary can catch it. A single corrupt value under RUBIX-TOKEN used to
  take the whole bundle down to a blank page that no amount of navigating could fix; the only way
  out was clearing site data by hand. A token we cannot read is simply no token.
*/
const safeDecode = (rawToken) => {
  if (!rawToken) return null;
  try {
    return jwtDecode(rawToken);
  } catch (err) {
    return null;
  }
};

const storedToken = getLocalStorage(prefixedKey(TOKEN_STORAGE_KEY));
const initialDecodeUser = safeDecode(storedToken);

const initialState = {
  ...(initialDecodeUser ?? {}),
  // An unreadable token is dropped here rather than carried around as a session that cannot work.
  token: initialDecodeUser ? storedToken : null,
  isForcedLogout: false
};

/*
  Logging out has to drop the decoded JWT claims too, not just the token: they are spread across the
  top level of this state (_id, exp, role...), and leaving them behind means the next session starts
  with the previous user's identity still readable.
*/
const LOGGED_OUT_STATE = {
  token: null,
  isForcedLogout: false
};

const authReducer = (state = initialState, action) => {
  switch (action.type) {
    case `${LOGIN}_FULFILLED`: {
      const decodeUser = safeDecode(action.payload.token);
      // A token the client cannot read is not a usable session: stay logged out rather than throw
      // inside the reducer, which would take down the login page itself.
      if (!decodeUser) return { ...LOGGED_OUT_STATE };
      return {
        ...state,
        ...decodeUser,
        token: action.payload.token,
        isForcedLogout: false
      };
    }

    case INIT_COMPLETE:
      return {
        ...state,
        fullInit: true
      };

    /*
      isForcedLogout MUST go back to false here. It used to be left as-is, and nothing but a
      successful login ever cleared it — so once a 401 had forced a logout, the layouts' "session
      expired" branch stayed latched on and swallowed every later navigation attempt, including the
      one the header's Log out button relies on. That is what made it impossible to log out.
    */
    case LOGOUT:
      return { ...LOGGED_OUT_STATE };

    case FORCE_LOGOUT:
      return {
        ...LOGGED_OUT_STATE,
        // True if 401 when user logged in, false otherwise (to avoid displaying message when receiving 401 in logging)
        isForcedLogout: !!state.token
      };

    default:
      return state;
  }
};

export default withSyncStorage(authReducer, 'token', TOKEN_STORAGE_KEY);
